import * as _ from 'lodash';

import Icon, { GreenCheck, icons } from '../../../generic/Icon';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import Tooltip from '../../../generic/Tooltip';
import businessFeatureMap from './businessFeatures';
import type { FeatureGroups, FullFeatureDescriptions, FullTierDescription } from './consts';
import { contributingDevelopers, featureDescriptions } from './consts';
import enterpriseFeatureMap from './enterpriseFeatures';
import freeFeatureMap from './freeFeatures';

const filteredMainFeatures = { ...featureDescriptions.mainFeatures };
//  @ts-expect-error - false positive err - don't display "contributingDevelopers" on the old billing page
delete filteredMainFeatures[contributingDevelopers];

type FeatureTableProps = {
  title: string;
  featureDetails: FullFeatureDescriptions[FeatureGroups];
  freeFeatures: FullTierDescription[FeatureGroups];
  businessFeatures: FullTierDescription[FeatureGroups];
  enterpriseFeatures: FullTierDescription[FeatureGroups];
};

const FeatureDetail = ({ value }: { value: string | boolean }) => {
  if (_.isString(value)) {
    return <span>{value}</span>;
  }

  if (value) {
    return <GreenCheck size={ICON_SIZE.LG} />;
  }

  return (
    <span className="feature-disabled">
      <Icon icon={icons.times} size={ICON_SIZE.LG} />
    </span>
  );
};

const FeatureTable = ({
  title,
  featureDetails,
  freeFeatures,
  businessFeatures,
  enterpriseFeatures,
}: FeatureTableProps) => {
  const rows = Object.keys(featureDetails).map((key) => (
    <div className="feature-row">
      <span>
        {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
        {featureDetails[key].name}
        {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
        {featureDetails[key]?.tooltipText && (
          // @ts-expect-error We know that this will exist because of the strict typings of the feature objects
          <Tooltip tooltip={featureDetails[key].tooltipText}>
            <span className="padding-sm">
              <Icon icon={icons.infoCircle} />
            </span>
          </Tooltip>
        )}
      </span>
      {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
      <FeatureDetail value={freeFeatures[key]} />
      {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
      <FeatureDetail value={businessFeatures[key]} />
      {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
      <FeatureDetail value={enterpriseFeatures[key]} />
    </div>
  ));

  return (
    <div className="subscription-feature-table">
      <div className="features-title">{title}</div>
      {rows}
    </div>
  );
};

const FullFeatureTable = () => (
  <>
    <FeatureTable
      title="Main Features"
      featureDetails={filteredMainFeatures}
      freeFeatures={freeFeatureMap.mainFeatures}
      businessFeatures={businessFeatureMap.mainFeatures}
      enterpriseFeatures={enterpriseFeatureMap.mainFeatures}
    />
    <FeatureTable
      title="Code Scanning"
      featureDetails={featureDescriptions.codeScanningFeatures}
      freeFeatures={freeFeatureMap.codeScanningFeatures}
      businessFeatures={businessFeatureMap.codeScanningFeatures}
      enterpriseFeatures={enterpriseFeatureMap.codeScanningFeatures}
    />
    <FeatureTable
      title="Security"
      featureDetails={featureDescriptions.securityFeatures}
      freeFeatures={freeFeatureMap.securityFeatures}
      businessFeatures={businessFeatureMap.securityFeatures}
      enterpriseFeatures={enterpriseFeatureMap.securityFeatures}
    />
    <FeatureTable
      title="Compliance"
      featureDetails={featureDescriptions.complianceFeatures}
      freeFeatures={freeFeatureMap.complianceFeatures}
      businessFeatures={businessFeatureMap.complianceFeatures}
      enterpriseFeatures={enterpriseFeatureMap.complianceFeatures}
    />
    <FeatureTable
      title="Quality"
      featureDetails={featureDescriptions.qualityFeatures}
      freeFeatures={freeFeatureMap.qualityFeatures}
      businessFeatures={businessFeatureMap.qualityFeatures}
      enterpriseFeatures={enterpriseFeatureMap.qualityFeatures}
    />
    <FeatureTable
      title="SBOM"
      featureDetails={featureDescriptions.sbomFeatures}
      freeFeatures={freeFeatureMap.sbomFeatures}
      businessFeatures={businessFeatureMap.sbomFeatures}
      enterpriseFeatures={enterpriseFeatureMap.sbomFeatures}
    />
    <FeatureTable
      title="Reporting"
      featureDetails={featureDescriptions.reportingFeatures}
      freeFeatures={freeFeatureMap.reportingFeatures}
      businessFeatures={businessFeatureMap.reportingFeatures}
      enterpriseFeatures={enterpriseFeatureMap.reportingFeatures}
    />
    <FeatureTable
      title="Admin Controls"
      featureDetails={featureDescriptions.adminControlFeatures}
      freeFeatures={freeFeatureMap.adminControlFeatures}
      businessFeatures={businessFeatureMap.adminControlFeatures}
      enterpriseFeatures={enterpriseFeatureMap.adminControlFeatures}
    />
    <FeatureTable
      title="Customer Success and Technical Support"
      featureDetails={featureDescriptions.supportFeatures}
      freeFeatures={freeFeatureMap.supportFeatures}
      businessFeatures={businessFeatureMap.supportFeatures}
      enterpriseFeatures={enterpriseFeatureMap.supportFeatures}
    />
    <FeatureTable
      title="Deployment"
      featureDetails={featureDescriptions.deploymentFeatures}
      freeFeatures={freeFeatureMap.deploymentFeatures}
      businessFeatures={businessFeatureMap.deploymentFeatures}
      enterpriseFeatures={enterpriseFeatureMap.deploymentFeatures}
    />
  </>
);

export default FullFeatureTable;
